/**
 * Shared GenUI renderer — production-safe subset extracted from
 * src/pages/genui.tsx. Renders a PageNode schema with the same page-level
 * wrapper styles used on the dev test page, but WITHOUT drag-and-drop or
 * editable behaviour (those stay in the dev page).
 *
 * Supports the FinalNode subset: page, container, row, column, grid,
 * heading, text, rich-text, image, icon, metric-card, chart, table,
 * list, card, timeline.
 */
import { DynamicIcon, type IconName } from "lucide-react/dynamic";
import { type CSSProperties, type ReactNode } from "react";
import Markdown from "react-markdown";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

// ────────────────────────────────────────────────────────
// Types & helpers
// ────────────────────────────────────────────────────────

type SchemaNode = Record<string, any>;

const DEFAULT_COLORS = [
  "#3b82f6",
  "#f97316",
  "#16a34a",
  "#e11d48",
  "#8b5cf6",
  "#06b6d4",
  "#eab308",
  "#ec4899",
  "#14b8a6",
  "#f59e0b",
];

function formatValue(value: unknown, format?: string): string {
  if (value == null) return "";
  switch (format) {
    case "currency": {
      const n = Number(value);
      return Number.isFinite(n)
        ? new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          }).format(n)
        : String(value);
    }
    case "percent": {
      const n = Number(value);
      return Number.isFinite(n) ? `${n}%` : String(value);
    }
    case "number": {
      const n = Number(value);
      return Number.isFinite(n) ? new Intl.NumberFormat("en-US").format(n) : String(value);
    }
    case "date": {
      const d = new Date(value as string);
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
    }
    default:
      return String(value);
  }
}

// Strip default <p> margins from markdown content so parent style wins.
const INLINE_MARKDOWN_COMPONENTS = {
  p: ({ children }: { children?: ReactNode }) => <>{children}</>,
};

// ────────────────────────────────────────────────────────
// Icon
// ────────────────────────────────────────────────────────

function SchemaIcon({
  name,
  size = 16,
  color,
  style,
}: {
  name?: string;
  size?: number;
  color?: string;
  style?: CSSProperties;
}) {
  if (!name) return null;
  return (
    <DynamicIcon
      name={name as IconName}
      size={size}
      color={color}
      style={style}
    />
  );
}

// ────────────────────────────────────────────────────────
// Chart
// ────────────────────────────────────────────────────────

function ChartBlock({ node }: { node: SchemaNode }) {
  const {
    chartType,
    data = [],
    xKey,
    yKey,
    labelKey,
    valueKey,
    seriesLabels,
    colors = DEFAULT_COLORS,
    showLegend,
    showGrid = true,
    stacked,
    fill,
    xLabel,
    yLabel,
    title,
    style,
  } = node as {
    chartType: string;
    data?: any[];
    xKey?: string;
    yKey?: string | string[];
    labelKey?: string;
    valueKey?: string;
    seriesLabels?: string[];
    colors?: string[];
    showLegend?: boolean;
    showGrid?: boolean;
    stacked?: boolean;
    fill?: boolean;
    xLabel?: string;
    yLabel?: string;
    title?: string;
    style?: CSSProperties;
  };

  const yKeys: string[] = Array.isArray(yKey) ? yKey : yKey ? [yKey] : [];
  const legend = showLegend ?? yKeys.length > 1;

  const body = (() => {
    switch (chartType) {
      case "line":
      case "area": {
        const ChartCmp = chartType === "area" ? AreaChart : LineChart;
        const SeriesCmp: any = chartType === "area" ? Area : Line;
        return (
          <ChartCmp data={data} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis
              dataKey={xKey}
              label={xLabel ? { value: xLabel, position: "insideBottom", offset: -5 } : undefined}
            />
            <YAxis
              label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft" } : undefined}
            />
            <Tooltip />
            {legend && <Legend />}
            {yKeys.map((yk, i) => (
              <SeriesCmp
                key={yk}
                dataKey={yk}
                name={seriesLabels?.[i] ?? yk}
                type="monotone"
                stroke={colors[i % colors.length]}
                fill={colors[i % colors.length]}
                fillOpacity={chartType === "area" || fill ? 0.3 : 0}
                strokeWidth={2}
                dot={false}
                stackId={stacked ? "stack" : undefined}
              />
            ))}
          </ChartCmp>
        );
      }
      case "bar":
      case "horizontal-bar": {
        const horizontal = chartType === "horizontal-bar";
        return (
          <BarChart
            data={data}
            layout={horizontal ? "vertical" : "horizontal"}
            margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            {horizontal ? (
              <>
                <XAxis type="number" />
                <YAxis dataKey={xKey} type="category" />
              </>
            ) : (
              <>
                <XAxis dataKey={xKey} />
                <YAxis />
              </>
            )}
            <Tooltip />
            {legend && <Legend />}
            {yKeys.map((yk, i) => (
              <Bar
                key={yk}
                dataKey={yk}
                name={seriesLabels?.[i] ?? yk}
                fill={colors[i % colors.length]}
                stackId={stacked ? "stack" : undefined}
              />
            ))}
          </BarChart>
        );
      }
      case "pie":
      case "doughnut": {
        return (
          <PieChart>
            <Tooltip />
            {showLegend !== false && <Legend />}
            <Pie
              data={data}
              dataKey={valueKey as string}
              nameKey={labelKey as string}
              innerRadius={chartType === "doughnut" ? "50%" : 0}
              outerRadius="80%"
              label
            >
              {data.map((_: any, i: number) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Pie>
          </PieChart>
        );
      }
      case "scatter": {
        return (
          <ScatterChart margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis dataKey={xKey} type="number" name={xLabel} />
            <YAxis dataKey={yKeys[0]} type="number" name={yLabel} />
            <Tooltip cursor={{ strokeDasharray: "3 3" }} />
            <Scatter data={data} fill={colors[0]} />
          </ScatterChart>
        );
      }
      case "radar": {
        return (
          <RadarChart data={data}>
            <PolarGrid />
            <PolarAngleAxis dataKey={xKey} />
            <Tooltip />
            {legend && <Legend />}
            {yKeys.map((yk, i) => (
              <Radar
                key={yk}
                name={seriesLabels?.[i] ?? yk}
                dataKey={yk}
                stroke={colors[i % colors.length]}
                fill={colors[i % colors.length]}
                fillOpacity={0.3}
              />
            ))}
          </RadarChart>
        );
      }
      case "bubble": {
        return (
          <ScatterChart margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis dataKey={xKey} type="number" />
            <YAxis dataKey={yKeys[0]} type="number" />
            <ZAxis dataKey={yKeys[1] ?? "size"} range={[50, 400]} />
            <Tooltip />
            <Scatter data={data} fill={colors[0]} />
          </ScatterChart>
        );
      }
      default:
        return (
          <div style={{ fontSize: 12, color: "#991b1b" }}>
            Unsupported chartType: {String(chartType)}
          </div>
        );
    }
  })();

  const wrapperStyle: CSSProperties = { width: "100%", minHeight: 300, ...style };

  return (
    <div style={wrapperStyle}>
      {title && <div style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>}
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          {body as any}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
// Table
// ────────────────────────────────────────────────────────

function TableBlock({ node }: { node: SchemaNode }) {
  const {
    title,
    columns = [],
    rows = [],
    striped,
    compact,
    style,
  } = node as {
    title?: string;
    columns?: Array<{
      key: string;
      label: string;
      align?: "left" | "center" | "right";
      width?: string;
      format?: string;
    }>;
    rows?: Array<Record<string, unknown>>;
    striped?: boolean;
    compact?: boolean;
    style?: CSSProperties;
  };

  const pad = compact ? "6px 8px" : "10px 12px";

  return (
    <div style={style}>
      {title && <div style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>}
      <div style={{ overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ backgroundColor: "#f9fafb" }}>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    textAlign: col.align ?? "left",
                    padding: pad,
                    fontWeight: 600,
                    borderBottom: "1px solid #e5e7eb",
                    width: col.width,
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr
                key={ri}
                style={{
                  backgroundColor: striped && ri % 2 === 1 ? "#f9fafb" : undefined,
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      textAlign: col.align ?? "left",
                      padding: pad,
                      verticalAlign: "top",
                    }}
                  >
                    {formatValue(row[col.key], col.format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
// Recursive renderer
// ────────────────────────────────────────────────────────

function renderNode(node: SchemaNode | null | undefined, key?: string | number): ReactNode {
  if (!node || typeof node !== "object") return null;
  const style: CSSProperties = (node.style as CSSProperties) ?? {};
  const children: SchemaNode[] = Array.isArray(node.children) ? node.children : [];

  switch (node.type) {
    case "page":
      return (
        <div
          key={key}
          style={{
            display: "flex",
            flexDirection: "column",
            marginLeft: "auto",
            marginRight: "auto",
            width: "100%",
            ...style,
          }}
        >
          {children.map((c, i) => renderNode(c, i))}
        </div>
      );

    case "container":
      return (
        <div
          key={key}
          style={{ display: "flex", flexDirection: "column", ...style }}
        >
          {children.map((c, i) => renderNode(c, i))}
        </div>
      );

    case "row":
      return (
        <div
          key={key}
          style={{ display: "flex", flexDirection: "row", overflowX: "auto", ...style }}
        >
          {children.map((c, i) => renderNode(c, i))}
        </div>
      );

    case "column":
      return (
        <div
          key={key}
          style={{ display: "flex", flexDirection: "column", ...style }}
        >
          {children.map((c, i) => renderNode(c, i))}
        </div>
      );

    case "grid": {
      const gridStyle: CSSProperties = { display: "grid", ...style };
      if (node.templateColumns) {
        gridStyle.gridTemplateColumns = node.templateColumns;
      } else if (node.columns) {
        gridStyle.gridTemplateColumns = `repeat(${node.columns}, minmax(0, 1fr))`;
      }
      return (
        <div key={key} style={{ ...gridStyle, overflowX: "auto" }}>
          {children.map((c, i) => renderNode(c, i))}
        </div>
      );
    }

    case "heading": {
      const level = Math.min(6, Math.max(1, Number(node.level) || 2));
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;
      const sizes: Record<number, string> = {
        1: "2rem",
        2: "1.5rem",
        3: "1.25rem",
        4: "1.125rem",
        5: "1rem",
        6: "0.875rem",
      };
      return (
        <Tag
          key={key}
          style={{ fontWeight: 700, lineHeight: 1.2, fontSize: sizes[level], margin: 0, ...style }}
        >
          {node.content}
        </Tag>
      );
    }

    case "text":
      return (
        <div key={key} style={style}>
          <Markdown components={INLINE_MARKDOWN_COMPONENTS}>
            {String(node.content ?? "")}
          </Markdown>
        </div>
      );

    case "rich-text":
      return (
        <div key={key} style={style} className="prose prose-sm max-w-none">
          {(node.blocks ?? []).map((b: any, i: number) => {
            if (b.type === "paragraph") {
              return <Markdown key={i}>{String(b.content ?? "")}</Markdown>;
            }
            if (b.type === "bullet-list") {
              return (
                <ul key={i}>
                  {(b.items ?? []).map((it: string, j: number) => (
                    <li key={j}>
                      <Markdown components={INLINE_MARKDOWN_COMPONENTS}>
                        {String(it)}
                      </Markdown>
                    </li>
                  ))}
                </ul>
              );
            }
            if (b.type === "numbered-list") {
              return (
                <ol key={i}>
                  {(b.items ?? []).map((it: string, j: number) => (
                    <li key={j}>
                      <Markdown components={INLINE_MARKDOWN_COMPONENTS}>
                        {String(it)}
                      </Markdown>
                    </li>
                  ))}
                </ol>
              );
            }
            if (b.type === "blockquote") {
              return (
                <blockquote key={i}>
                  <Markdown>{String(b.content ?? "")}</Markdown>
                </blockquote>
              );
            }
            return null;
          })}
        </div>
      );

    case "image":
      return (
        <figure key={key} style={{ margin: 0, ...style }}>
          <img
            src={node.src}
            alt={node.alt ?? ""}
            style={{
              width: "100%",
              display: "block",
              objectFit: (node.objectFit as CSSProperties["objectFit"]) ?? "cover",
              borderRadius: style.borderRadius as CSSProperties["borderRadius"],
              maxHeight: style.maxHeight as CSSProperties["maxHeight"],
            }}
          />
          {node.caption && (
            <figcaption style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              {node.caption}
            </figcaption>
          )}
        </figure>
      );

    case "icon":
      return (
        <SchemaIcon
          key={key}
          name={node.name}
          size={node.size ?? 16}
          color={node.color}
          style={style}
        />
      );

    case "metric-card": {
      const dirColor =
        node.trendDirection === "up"
          ? "#16a34a"
          : node.trendDirection === "down"
          ? "#dc2626"
          : "#6b7280";
      return (
        <div
          key={key}
          style={{
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            padding: 16,
            backgroundColor: "#fff",
            ...style,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "#6b7280",
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {node.icon && <SchemaIcon name={node.icon} size={14} />}
            <span>{node.label}</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{node.value}</div>
          {node.trend && (
            <div style={{ fontSize: 12, color: dirColor, marginTop: 2 }}>{node.trend}</div>
          )}
        </div>
      );
    }

    case "chart":
      return <ChartBlock key={key} node={node} />;

    case "table":
      return <TableBlock key={key} node={node} />;

    case "list": {
      const variant = node.variant ?? "bullet";
      const Tag = variant === "numbered" ? "ol" : "ul";
      return (
        <Tag key={key} style={{ paddingLeft: 20, margin: 0, ...style }}>
          {(node.items ?? []).map((it: any, i: number) => {
            const isObj = typeof it === "object" && it !== null;
            const content = isObj ? it.content : String(it);
            const description = isObj ? it.description : undefined;
            const checked = isObj ? it.checked : undefined;
            return (
              <li
                key={i}
                style={
                  variant === "checklist"
                    ? { listStyle: "none", marginLeft: -20 }
                    : { marginBottom: 4 }
                }
              >
                {variant === "checklist" && (
                  <input
                    type="checkbox"
                    checked={!!checked}
                    readOnly
                    style={{ marginRight: 8 }}
                  />
                )}
                <span>
                  <Markdown components={INLINE_MARKDOWN_COMPONENTS}>
                    {String(content ?? "")}
                  </Markdown>
                </span>
                {description && (
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    <Markdown components={INLINE_MARKDOWN_COMPONENTS}>
                      {String(description)}
                    </Markdown>
                  </div>
                )}
              </li>
            );
          })}
        </Tag>
      );
    }

    case "card":
      return (
        <div
          key={key}
          style={{
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            backgroundColor: "#fff",
            ...style,
          }}
        >
          {(node.title || node.subtitle || node.headerRight) && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 12,
                padding: 16,
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <div>
                {node.title && (
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{node.title}</div>
                )}
                {node.subtitle && (
                  <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>
                    {node.subtitle}
                  </div>
                )}
              </div>
              {node.headerRight && <div>{renderNode(node.headerRight, "hdr")}</div>}
            </div>
          )}
          {node.children && (
            <div
              style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, overflowX: "auto" }}
            >
              {(node.children as SchemaNode[]).map((c, i) => renderNode(c, i))}
            </div>
          )}
          {node.footer && (
            <div
              style={{
                padding: 16,
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                gap: 8,
              }}
            >
              {(node.footer as SchemaNode[]).map((c, i) => renderNode(c, i))}
            </div>
          )}
        </div>
      );

    case "timeline": {
      const items: any[] = node.items ?? [];
      return (
        <ol
          key={key}
          style={{ listStyle: "none", paddingLeft: 0, margin: 0, ...style }}
        >
          {items.map((it, i) => {
            const statusColor =
              it.status === "completed"
                ? "#16a34a"
                : it.status === "active"
                ? "#3b82f6"
                : it.status === "error"
                ? "#dc2626"
                : "#9ca3af";
            return (
              <li
                key={i}
                style={{
                  display: "flex",
                  gap: 12,
                  paddingBottom: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: statusColor,
                      border: "2px solid #fff",
                      boxShadow: `0 0 0 2px ${statusColor}`,
                    }}
                  />
                  {i < items.length - 1 && (
                    <div
                      style={{
                        flex: 1,
                        width: 2,
                        backgroundColor: "#e5e7eb",
                        marginTop: 4,
                      }}
                    />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{it.title}</div>
                  {it.description && (
                    <div style={{ fontSize: 13, color: "#6b7280" }}>
                      {it.description}
                    </div>
                  )}
                  {it.timestamp && (
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                      {it.timestamp}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      );
    }

    default:
      return (
        <div
          key={key}
          style={{
            padding: 8,
            border: "1px dashed #fca5a5",
            color: "#991b1b",
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          Unknown node type: {String(node.type)}
        </div>
      );
  }
}

export { renderNode, ChartBlock, TableBlock, SchemaIcon, formatValue, INLINE_MARKDOWN_COMPONENTS };
export type { SchemaNode };

/**
 * Render a PageNode schema with the page-level wrapper styles used by
 * production tearsheets. Does NOT include drag-and-drop or editable
 * behaviour — those live in the /genui test page.
 */
export function GenUIRenderer({ schema }: { schema: SchemaNode | null | undefined }) {
  if (!schema) return null;
  return <>{renderNode(schema)}</>;
}
